import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { applyStatusBarTheme } from '../utils/capacitorPlugins'

const ThemeContext = createContext(null)

const STORAGE_KEY = 'nudgu:theme'

// Browser-chrome / native status-bar background per theme (kept in sync with
// --color-bg in src/styles/index.css).
const THEME_COLORS = {
  dark: '#0f111a',
  light: '#f5f6fa',
}

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'dark'
  } catch {
    return 'dark'
  }
}

/**
 * Applies the theme to the document root, persists it, and syncs the browser
 * theme-color meta tag + native status bar. Called on every theme change.
 */
function applyTheme(theme) {
  document.documentElement.dataset.theme = theme

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_COLORS[theme])

  applyStatusBarTheme(theme)
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readStoredTheme)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      /* ignore persistence failures (private mode, etc.) */
    }
    applyTheme(theme)
  }, [theme])

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'))
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- useTheme hook is co-located with its provider by convention
export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    // Fallback if used outside provider
    return { theme: 'dark', toggleTheme: () => {}, setTheme: () => {} }
  }
  return ctx
}
